import { ArrowRight, ChevronLeft } from 'lucide-react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { BeneficiaryView } from '../../../types/entity-types';
import { PaymentInitiationCard } from '../components/payment-initiation-card';
import { getLatestDebitTransferDate } from '../utils/get-latest-debit-transfer-date';
import { BeneficiaryTable } from './beneficiary-table';

type ViewAllRecipientsConfig =
  | {
      visible: false;
    }
  | {
      visible: true;
      onGoBack: () => void;
    };

type PaymentsSectionProps = {
  beneficiaries: BeneficiaryView[];
  viewAllRecipientsConfig: ViewAllRecipientsConfig;
  onInitiateBillPay: () => void;
  onInitiateInternalTransfer: () => void;
  onInitiateExternalTransfer: () => void;
  onInitiateBeneficiaryPayment: (beneficiaryId: string) => void;
  onViewAllRecipients: () => void;
};

export const PaymentsSection = ({
  beneficiaries,
  viewAllRecipientsConfig,
  onInitiateBillPay,
  onInitiateInternalTransfer,
  onInitiateExternalTransfer,
  onInitiateBeneficiaryPayment,
  onViewAllRecipients,
}: PaymentsSectionProps) => {
  if (viewAllRecipientsConfig.visible) {
    return (
      <Stack align="stretch" className="bg-white px-32">
        <Inline space={3}>
          <Button
            variant="ghost"
            size="icon"
            className="bg-seapoint-gray-background-modal"
            onClick={viewAllRecipientsConfig.onGoBack}
            aria-label="Go back"
          >
            <div className="h-9 w-9 rounded-full bg-seapoint-gray-background-modal p-1">
              <ChevronLeft className="h-full w-full" />
            </div>
          </Button>

          <h1 className="text-2xl font-semibold text-black">All recipients</h1>
        </Inline>

        <BeneficiaryTable
          beneficiaries={beneficiaries}
          config={{
            showPagination: true,
            sortVariant: 'name',
            showSearch: true,
          }}
          onInitiateBeneficiaryPayment={onInitiateBeneficiaryPayment}
          className="pt-0"
        />
      </Stack>
    );
  }

  const beneficiariesWithLastPaid = beneficiaries.filter((b) => {
    const latestDebitTransferDate = getLatestDebitTransferDate({
      transactions: b.transactions,
    });

    return !!latestDebitTransferDate;
  });

  return (
    <Stack align="stretch" space={10} className="bg-white px-32 pb-5">
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Payments</h1>
      </Inline>

      <Inline space={3} wrap="nowrap" align="stretch" className="gap-y-3">
        <PaymentInitiationCard variant="BILL_PAY" onInitiatePayment={onInitiateBillPay} />

        <PaymentInitiationCard variant="EXTERNAL_TRANSFER" onInitiatePayment={onInitiateExternalTransfer} />

        <PaymentInitiationCard variant="INTERNAL_TRANSFER" onInitiatePayment={onInitiateInternalTransfer} />
      </Inline>

      {beneficiaries.length > 0 && (
        <Stack align="stretch">
          <h2 className="text-lg font-semibold text-black">Most recent recipients</h2>

          <BeneficiaryTable
            beneficiaries={beneficiariesWithLastPaid}
            onInitiateBeneficiaryPayment={onInitiateBeneficiaryPayment}
            className="gap-0 pt-0"
          />

          <Stack>
            <Button
              variant="link"
              iconRight={<ArrowRight className="h-4 w-4" />}
              size="none"
              className="text-seapoint-blue"
              onClick={onViewAllRecipients}
            >
              View all recipients
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
